/**
 * URL Parameter Names - centralized constants for URL query parameters
 */
export const URL_PARAMS = {
  // Main tab parameter
  TAB: 'tab',

  // Subtab parameters
  SUBTAB: 'subtab',
  TOOL: 'tool',

  // Pagination parameters
  PAGE: 'page',
  PROGRAM_PAGE: 'programPage',

  // Filter parameters
  SEARCH: 'search',
  SORT: 'sort',
  SORT_ORDER: 'sortOrder',
  PROGRAM_FILTER: 'program',
  PROGRAMS: 'programs', // Landing Page: selected programs filter for teams tab
  TEAM_FILTER: 'team',
  STATUS_FILTER: 'status',
  MODE: 'mode',
  EMPLOYEE_ID: 'employeeId',
  EMPLOYEE_IDS: 'employeeIds', // People Assignment: comma-separated employee ids for the bulk-assign journey

  // Manage Request modal parameters
  /** When 'other', opens the Manage Request modal on the "Other requests" tab (used for email deeplinks). */
  REQUEST_TAB: 'requestTab',
  EMAIL: 'email',
  /** When 'true', auto-opens the Manage Request modal on load (used for deeplinks). */
  OPEN_MANAGE_REQUEST: 'openManageRequest',

  // Navigation parameters (for pages with sidebar navigation)
  CATEGORY: 'category',
} as const

/**
 * Parameters that should be cleared when the main tab changes
 * This ensures a clean state when switching between major sections
 * Includes filters, pagination, subtabs, and other tab-specific state
 */
export const SECONDARY_PARAMS = [
  URL_PARAMS.SUBTAB,
  URL_PARAMS.TOOL,
  URL_PARAMS.SEARCH,
  URL_PARAMS.SORT,
  URL_PARAMS.SORT_ORDER,
  URL_PARAMS.PROGRAM_FILTER,
  URL_PARAMS.PROGRAMS,
  URL_PARAMS.TEAM_FILTER,
  URL_PARAMS.STATUS_FILTER,
  URL_PARAMS.MODE,
  URL_PARAMS.EMPLOYEE_ID,
  URL_PARAMS.EMPLOYEE_IDS,
  URL_PARAMS.REQUEST_TAB,
  URL_PARAMS.OPEN_MANAGE_REQUEST,
  URL_PARAMS.PAGE,
  URL_PARAMS.PROGRAM_PAGE,
] as const

/**
 * Default values for URL parameters
 */
export const URL_PARAM_DEFAULTS = {
  PAGE: 1,
} as const

/**
 * Tab ID constants - centralized tab identifiers used across the application
 */
export const TAB_IDS = {
  // Landing Page tabs
  LANDING: {
    TEAM: 'team',
    PROGRAM: 'program',
  },

  // Metrics Settings tabs
  METRICS_SETTINGS: {
    DASHBOARDS: 'dashboards',
    SE_METRICS: 'se-metrics',
    GLOBAL_KPI: 'global-kpi',
  },

  // QA Metrics subtabs (Team Dashboard)
  QA_METRICS: {
    TRENDS: 'trends',
    AUTOMATION: 'automation',
  },

  // QA Metrics subtabs (Program Dashboard)
  PROGRAM_QA_METRICS: {
    BUGS_OVERVIEW: 'bugs-overview',
    AUTOMATION_TRENDS: 'automation-trends',
  },

  // AI Insights subtabs (Program Dashboard)
  AI_INSIGHTS: {
    AI_TRENDS: 'ai-trends',
    TEAMS_PRODUCTIVITY: 'teams-productivity',
  },

  // AI Productivity page
  AI_PRODUCTIVITY: {
    CURRENT_OVERVIEW: 'current-overview',
    TRENDS_OVERVIEW: 'trends-overview',
  },

  // AI Tools
  AI_TOOLS: {
    WINDSURF: 'windsurf',
    COPILOT: 'copilot',
  },

  // EMD Trends tabs
  EMD_TRENDS: {
    GLOBAL: 'global',
    PI: 'pi',
    SPRINT: 'sprint',
    DEV: 'dev',
    PR: 'pr',
    COMBINED: 'combined',
  },

  // Manage Resources (ManageTag) - no tabs currently
  MANAGE_TAG: {
    LICENSES: 'licenses',
  },

  // Manage Roles & Responsibilities tabs
  MANAGE_ROLES: {
    ROLES: 'roles',
  },
} as const

// Type exports for type safety
export type UrlParamKey = keyof typeof URL_PARAMS
export type UrlParamValue = (typeof URL_PARAMS)[UrlParamKey]
